import React, { useEffect, useState, ReactElement, FC } from 'react'
import { useSession } from 'next-auth/react'
import { useTranslation } from 'next-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TrophyDetailCardAttributesList } from '~/components/TrophyDetailCard/TrophyDetailCardAttributesList'
import { faAngleDown, faCertificate, faCopy, faShareNodes } from '@fortawesome/pro-regular-svg-icons'
import { ShareProductServiceInterface } from '~/modules/Products/Domain/ShareProductServiceInterface'
import { ShareProductService } from '~/modules/Products/Infrastructure/ShareProductService'
import { SegmentAnalyticsService } from '~/modules/Analytics/Infrastructure/SegmentAnalyticsService'
import { ShareProductDto } from '~/modules/Products/Domain/ShareProductDto'
import { TrophyDetailComponentDto } from '~/modules/Trophies/Infrastructure/TrophyDetailComponentDto'
import { MintButton } from './MintButton'
import styles from './TrophyDetailCard.module.scss'

interface Props {
  trophyDetailDto: TrophyDetailComponentDto
}

export const TrophyDetailCard: FC<Props> = ({ trophyDetailDto }) => {
  const { t } = useTranslation('trophy_details')
  const session = useSession()
  const [openDescription, setOpenDescription] = useState(false)
  const [shareService, setShareService] = useState<ShareProductServiceInterface | undefined>()
  const verifyButton = (
    trophyDetailDto.mintStatus !== 'minted' ||
    !trophyDetailDto.collection ||
    !trophyDetailDto.tokenId
  )
    ? null
    : (
        <a
          // eslint-disable-next-line max-len
          href={ `https://verify.belegends.com/check?verificationString=${trophyDetailDto.collection.contractChain}/${trophyDetailDto.collection.contractAddress}/${trophyDetailDto.tokenId}` }
          target="_blank"
          rel="noreferrer"
          className={ styles.trophyDetailCard__button }
        >
          <FontAwesomeIcon icon={ faCertificate }/> { }
          { t('verify_button_title') }
        </a>
      )

  useEffect(() => {
    if (window !== undefined && shareService === undefined) {
      setShareService(
        new ShareProductService(
          window.navigator,
          new SegmentAnalyticsService(window.analytics)
        )
      )
    }
  }, [shareService])

  let media
  let shareProductDto: ShareProductDto
  let shareButton: ReactElement | null = null

  if (shareService && shareService.canShare() && trophyDetailDto.id !== null) {
    shareProductDto = {
      title: `Legend Museum | ${trophyDetailDto.name}`,
      url: window.location.href,
      analytics: {
        productId: trophyDetailDto.id.toString(),
        organizer: trophyDetailDto.organizer.name,
        category: 'trophy',
        collection: trophyDetailDto.competition,
      },
    }

    shareButton = (
      <button
        data-testid={ shareService.isShareApiAvailable() ? 'share-button' : 'copy-button' }
        className={ styles.trophyDetailCard__button }
        onClick={ () => shareService.share(shareProductDto) }
      >
        <FontAwesomeIcon
          icon={ shareService.isShareApiAvailable() ? faShareNodes : faCopy }
        />

        { ` ${t(shareService.isShareApiAvailable() ? 'share_button_title' : 'copy_button_title')}` }
      </button>
    )
  }

  if (trophyDetailDto.animation) {
    media = (
      <video
        data-qa='trophy-animation'
        className={ styles.trophyDetailCard__trophyImage }
        poster={ trophyDetailDto.rawImageUrl }
        loop={ true }
        autoPlay={ true }
        playsInline={ true }
        muted={ true }
      >
        <source
          src={ trophyDetailDto.animation.url }
          type={ trophyDetailDto.animation.type }
        />
      </video>
    )
  } else {
    media = (
      <img
        data-qa='trophy-image'
        src={ trophyDetailDto.rawImageUrl }
        alt={ t('trophy_details_image_alt') }
        className={ styles.trophyDetailCard__trophyImage }
      />
    )
  }

  return (
    <div className={ styles.trophyDetailCard__container }>
      <div className={ styles.trophyDetailCard__buttons }>
        { verifyButton }

        { shareButton }
      </div>

      <h1 className={ styles.trophyDetailCard__trophyTitle }>
        { trophyDetailDto.title }

        <small className={ styles.trophyDetailCard__trophySubtitle }>
          { trophyDetailDto.subtitle }
        </small>
      </h1>

      {
        trophyDetailDto.mintStatus === 'pending' &&
        session.status === 'authenticated' &&
        session.data.user.id === trophyDetailDto.userId &&
          <MintButton trophy={ trophyDetailDto } />
      }

      { media }

      <strong className={ styles.trophyDetailCard__trophyOrganizer }>
        { trophyDetailDto.organizer.name }

        <small className={ styles.trophyDetailCard__trophyCompetition }>
          { trophyDetailDto.competition }
        </small>
      </strong>

      <TrophyDetailCardAttributesList attributesDto={ trophyDetailDto.attributes } />

      <h2
        data-qa='trophy-description-title'
        className={ `
          ${styles.trophyDetailCard__descriptionTitle}
          ${openDescription && styles.trophyDetailCard__descriptionTitle_open}
        ` }
        onClick={ () => setOpenDescription(!openDescription) }
      >
        { t('description_title') }
        <FontAwesomeIcon
          icon={ faAngleDown }
          rotation={ openDescription ? 180 : undefined }
        />
      </h2>

      <div
        data-qa='trophy-description'
        className={ `
          ${styles.trophyDetailCard__trophyDescription}
          ${openDescription && styles.trophyDetailCard__trophyDescription_open}
        ` }
      >
        { trophyDetailDto.description }
      </div>
    </div>
  )
}
